import { Button } from '@mantine/core';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FC, useEffect, useMemo, useState } from 'react';

import BaseModal from '@/components/BaseModal.tsx';
import ButtonSelection from '@/components/ButtonSelection.tsx';
import CircularLoader from '@/components/CircularLoader.tsx';
import Unavailable from '@/components/Unavailable.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { SLOT_ADJUSTMENT_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import useTimeslots from '@/hooks/useTimeslots.ts';

import {
    abortAvailabilityInteraction,
    settleAvailabilityInteraction,
    startAvailabilityInteraction,
} from '@/lib/availability-telemetry.ts';

import { Option } from '@/models/Option.ts';
import { Insurance } from '@/models/appointment/Appointment.ts';
import { EnrichedAppointmentType, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';
import { Timings } from '@/models/doc-cirrus/DCCalendar.ts';
import { AvailabilityMode } from '@/models/metrics.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { getTimeSlotInfo } from '@/utils/calendarUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

interface Props {
    opened: boolean;
    onClose: () => void;
    initialDate: string;
    selectedAppointmentType?: EnrichedAppointmentType;
    practitioner: Practitioner;
    selectedCalendar: string;
    insurance: Insurance;
    onSlotSelect: (slot: string) => void;
    /** Which booking flow opened this picker. */
    mode: AvailabilityMode;
}

const SlotAdjustmentModal: FC<Props> = ({
    opened,
    onClose,
    initialDate,
    selectedAppointmentType,
    practitioner,
    selectedCalendar,
    insurance,
    onSlotSelect,
    mode,
}) => {
    const isReturningPatient = useBookingStore((state) => state.is_returning_patient);
    const [selectedSlot, setSelectedSlot] = useState<string | undefined>();

    const hasAppointmentType = selectedAppointmentType !== undefined;

    // Must stay above useTimeslots: effects run in the order their hooks were called, and the hook
    // settles a cache hit on its very first effect. Started after it, the interaction would still be
    // closed when that settle ran, and would only ever end on the timeout.
    useEffect(() => {
        if (opened) {
            startAvailabilityInteraction({ key: SLOT_ADJUSTMENT_INTERACTION_KEY, mode: mode, action: 'open_slots' });

            // Nothing is requested without an appointment type, so nothing would ever settle this.
            if (!hasAppointmentType) {
                settleAvailabilityInteraction({
                    key: SLOT_ADJUSTMENT_INTERACTION_KEY,
                    outcome: 'failure',
                    reason: 'availability_config_incomplete',
                });
            }
        }

        // The operator closed the modal on a check that was still running.
        return () => abortAvailabilityInteraction(SLOT_ADJUSTMENT_INTERACTION_KEY);
    }, [opened, mode, hasAppointmentType]);

    // TODO: send end_date to filter less data on the backend
    const {
        availability,
        is_loading: isLoading,
        error,
        next_available_slot: nextAvailableSlot,
        start_date: startDate,
        pageTo,
    } = useTimeslots({
        practitioner: practitioner,
        insurance: insurance,
        mode: mode,
        interactionKey: SLOT_ADJUSTMENT_INTERACTION_KEY,
        appointmentType: selectedAppointmentType,
        isReturning: isReturningPatient,
        defaultStartDate: initialDate,
        selectedCalendarId: selectedCalendar,
    });

    // We only show one day at a time in this modal, so we take the first day from availability
    const currentDay = availability?.[0];
    const hasSlotsToday = (currentDay?.slots.length ?? 0) > 0;

    const calendarTimings = useMemo((): Timings | undefined => {
        const selected = practitioner.calendars.find((cal) => cal.pvs.id === selectedCalendar);
        if (selected?.pvs && 'timings' in selected.pvs) {
            return selected.pvs.timings;
        }
        if (selectedCalendar && practitioner.calendar_timings_map?.has(selectedCalendar)) {
            return practitioner.calendar_timings_map.get(selectedCalendar);
        }
        return undefined;
    }, [practitioner, selectedCalendar]);

    const handleConfirm = (): void => {
        if (!selectedSlot) return;
        onSlotSelect(selectedSlot);
        onClose();
    };

    const handlePrevDay = (): void => {
        const prev = dayjs(startDate).subtract(1, 'day');
        if (prev.isBefore(dayjs(), 'day')) return;
        pageTo(prev.format('YYYY-MM-DD'), 'previous_page');
        setSelectedSlot(undefined);
    };

    const handleNextDay = (): void => {
        pageTo(dayjs(startDate).add(1, 'day').format('YYYY-MM-DD'), 'next_page');
        setSelectedSlot(undefined);
    };

    const handleJumpToNextFree = (): void => {
        pageTo(dayjs(nextAvailableSlot).format('YYYY-MM-DD'), 'jump_next_free');
        setSelectedSlot(undefined);
    };

    const mapSlotsToOptions = (slots: string[]): Option[] =>
        slots.map((slot) => {
            const slotTime = dayjs(slot).format('HH:mm');
            const slotDate = dayjs(startDate).toDate();
            const slotInfo = calendarTimings ? getTimeSlotInfo(slotTime, calendarTimings, slotDate) : { segments: [] };
            const color = slotInfo.segments.length > 0 ? slotInfo.segments[0].color : undefined;

            return { id: slot, value: slot, label: slotTime, color: color };
        });

    const isPrevDisabled = dayjs(startDate).isSame(dayjs(), 'day') || dayjs(startDate).isBefore(dayjs(), 'day');

    return (
        <BaseModal opened={opened} onClose={onClose} title="Termin anpassen" size="lg" zIndex={300}>
            <div className="flex flex-col gap-6">
                {isLoading ? (
                    <div className="flex h-40 items-center justify-center">
                        <CircularLoader />
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-2">
                            <Paragraph className="text-sm font-medium text-slate-500">Datum anpassen</Paragraph>
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="size-9 !p-0"
                                    onClick={handlePrevDay}
                                    disabled={isPrevDisabled}
                                >
                                    <ChevronLeft className="size-5" />
                                </Button>
                                <span className="flex-grow text-center font-medium">
                                    {dayjs(startDate).format('dddd, D. MMMM YYYY')}
                                </span>
                                <Button variant="outline" size="sm" className="size-9 !p-0" onClick={handleNextDay}>
                                    <ChevronRight className="size-5" />
                                </Button>
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <Paragraph className="text-sm font-medium text-slate-500">
                                Verfügbare Termine ({selectedAppointmentType?.duration?.value ?? 30} Min.)
                            </Paragraph>
                            {error && <Unavailable text="Zeitfenster konnten nicht geladen werden" />}
                            {!error && hasSlotsToday && (
                                <ButtonSelection
                                    onSelect={setSelectedSlot}
                                    options={mapSlotsToOptions(
                                        currentDay?.slots.filter(
                                            (slot) => !dayjs(slot).isSame(dayjs(initialDate), 'minute')
                                        ) ?? []
                                    )}
                                    selected={selectedSlot}
                                    numberOfOptionsInRow={4}
                                />
                            )}
                            {!error && !hasSlotsToday && (
                                <div className="flex flex-col items-center gap-4 py-6">
                                    <Paragraph className="text-slate-500">
                                        Keine verfügbaren Termine an diesem Tag
                                    </Paragraph>
                                    {nextAvailableSlot && (
                                        <Button variant="outline" onClick={handleJumpToNextFree}>
                                            Nächstes Zeitfenster ({dayjs(nextAvailableSlot).format('D. MMM')})
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>
                        Abbrechen
                    </Button>
                    <Button onClick={handleConfirm} disabled={!selectedSlot}>
                        Termin aktualisieren
                    </Button>
                </div>
            </div>
        </BaseModal>
    );
};

export default SlotAdjustmentModal;
